<template>
  <div
    v-if="post"
    class="h-full w-full flex flex-col justify-end">
    <div class="flex justify-between items-end px-6 mb-4">
      <div class="w-5/6 drop-shadow">
        <h3 class="text-white font-bold flex items-center gap-1 text-[16px] mb-1">
          <router-link
            v-if="user.userId"
            :to="`/@${user.username}`"
            @click="$emit('avatar-clicked')">
            @{{ user?.username }}
          </router-link>

          <InlineSvg
            v-if="user.tikTokFollowerCount"
            :src="require('@/assets/svg/design/verified.svg')"
            class="p-0.5 w-5 h-5"/>
        </h3>

        <p class="text-caption text-white leading-5 play-prevent">
          <VRuntimeTemplate :template="processHashtags(post.description)"/>
        </p>
      </div>

      <div class="relative flex flex-col gap-3 items-center justify-center play-prevent">
        <router-link
          v-if="user.userId"
          class="mb-1"
          :to="`/@${user.username}`">
          <Avatar
            class="w-12 h-12 drop-shadow"
            :show-status="true"
            :user-item="user"
            @click="$emit('avatar-clicked');clientEventTrack({ typeName: 'post__profile_clicked', userId: user.userId })"/>
        </router-link>

        <!-- Views counter -->
        <div class="flex flex-col gap-1.5 items-center">
          <router-link v-if="isDev" :to="'/' + post.id">
            <InlineSvg
              :src="require('@/assets/fontawesome/solid/eye.svg')"
              class="text-white w-12"/>
          </router-link>

          <InlineSvg
            v-else
            :src="require('@/assets/fontawesome/solid/eye.svg')"
            class="text-white w-12"/>

          <span class="text-white drop-shadow text-[16px] font-bold">
            {{ countNumberFormat(post.viewCount) }}
          </span>
        </div>

        <!-- Share -->
        <div
          v-if="isAbleToShare"
          class="flex flex-col gap-1.5 items-center cursor-pointer"
          @click="share">
          <InlineSvg
            :src="require('@/assets/fontawesome/solid/share.svg')"
            class="text-white w-12"/>

          <span class="text-white drop-shadow text-[16px] font-bold">
            {{ countNumberFormat(post.shareCount) }}
          </span>
        </div>
      </div>
    </div>

    <div class="px-6 mb-10 flex justify-between items-end play-prevent">
      <!-- Previous slide -->
      <button
        type="button"
        class="bg-white rounded-full w-12 h-12 flex items-center justify-center"
        @click="$emit('slide-prev')">
        <InlineSvg
          :src="require('@/assets/fontawesome/regular/arrow-rotate-left.svg')"
          class="w-6 text-gray-400"/>
      </button>

      <!-- Next slide -->
      <button
        type="button"
        class="bg-white rounded-full w-17 h-17 flex items-center justify-center"
        @click="$emit('slide-next')">
        <InlineSvg
          :src="require('@/assets/fontawesome/regular/xmark.svg')"
          class="w-7 text-gray-400"/>
      </button>

      <!-- Like -->
      <button
        type="button"
        class="rounded-full w-17 h-17 flex items-center justify-center"
        :class="[post.liked ? 'bg-primary' : 'bg-white']"
        @click="like()">
        <InlineSvg
          :src="require('@/assets/fontawesome/solid/heart.svg')"
          :class="[post.liked ? 'text-white' : 'text-primary']"
          class="w-10"/>
      </button>

      <!-- Previous slide -->
      <router-link
        v-if="isAuthenticated"
        :to="`/chat/${user.userId}`"
        class="bg-white rounded-full w-12 h-12 flex items-center justify-center"
        @click="messageMe(user.userId); messageMeClicked(user.userId, 'post')">
        <InlineSvg
          :src="require('@/assets/fontawesome/regular/paper-plane-top.svg')"
          class="w-6 text-gray-400"/>
      </router-link>

      <button
        v-else
        type="button"
        class="bg-white rounded-full w-12 h-12 flex items-center justify-center"
        @click="showSignUp">
        <InlineSvg
          :src="require('@/assets/fontawesome/regular/paper-plane-top.svg')"
          class="w-6 text-gray-400"/>
      </button>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/Functional/Avatar'
import VRuntimeTemplate from 'vue3-runtime-template'
import { mapState, mapWritableState } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useAppStore } from '@/stores/app'
import { useSubscriptionsStore } from '@/stores/subscriptions'
import helpers from '@/helpers'
import { clientEventTrack } from '@/helpers/methods'

export default {
  name: 'PostHandlers',
  components: {
    Avatar,
    VRuntimeTemplate
  },
  props: {
    post: {
      type: Object,
      default: null,
      required: true
    },
    user: {
      type: Object,
      default: () => ({
        userId: null,
        username: null
      }),
      required: true
    },
    like: {
      type: Function,
      default: null
    },
    share: {
      type: Function,
      default: null
    },
    messageMe: {
      type: Function,
      default: null
    }
  },
  emits: [
    'avatar-clicked',
    'subscribed',
    'unlocked',
    'messageSent',
    'pause',
    'modal-closed',
    'slide-next',
    'slide-prev'
  ],
  data () {
    return {
      activeSub: null
    }
  },
  computed: {
    ...mapState(useAppStore, [
      'isiOS',
      'secretToken'
    ]),
    ...mapState(useAuthStore, [
      'loginIsVisible',
      'isAuthenticated',
      'isTokenExist',
      'options',
      'authOptions'
    ]),
    ...mapState(useUserStore, [
      'balance',
      'userId'
    ]),
    ...mapWritableState(useAuthStore, [
      'signupIsVisible',
      'pathToRedirect'
    ]),
    ...mapState(useSubscriptionsStore, [
      'recentSubscriptions',
      'userHasPaidSubscription'
    ]),
    showBadgeAfterSubscription () {
      return this.recentSubscriptions.includes(this.user.userId)
    },
    isAbleToShare () {
      return navigator.canShare
    },
    type () {
      return this.post.type === 1 ? this.$t('common.photo') : this.$t('common.video')
    },
    isDev () {
      return process.env.VUE_APP_IS_DEBUG === 'yes'
    },
    subscriptionPriceInCurrency () {
      return this.user.subscriptionPlans?.length ? this.user.subscriptionPlans[0].priceInCurrency : this.user.subscriptionPriceInCurrency
    },
    subscriptionPlansFirstPrice () {
      return this.user.subscriptionPlans?.length ? this.user.subscriptionPlans[0].priceInCurrency : null
    }
  },
  methods: {
    clientEventTrack: clientEventTrack,
    processHashtags (str) {
      //
      return str.replace(
        /#([\p{Letter}\d_]+)/gu,
        '<router-link class="font-semibold" :to="\'/discover/$1\'">#$1</router-link>'
      )
    },
    subscribe () {
      // If video post is partial - pause
      if (this.post.locked && this.post.videoUrl) {
        this.$emit('pause')
      }

      if (this.isTokenExist) {
        this.$refs.subscribeModal.openModal()
      } else {
        helpers.openSignInByEmailModal()
      }
    },
    unlock () {
      // If video post is partial - pause
      if (this.post.locked && this.post.videoUrl) {
        this.$emit('pause')
      }

      if (this.isTokenExist) {
        this.$refs.unlockPostContent.openModal()
      } else {
        helpers.openSignInByEmailModal()
      }
    },
    showSignUp ({ postId = null, userId = null }) {
      if (userId) {
        this.pathToRedirect = `/chat/${userId}`
      } else if (postId) {
        this.pathToRedirect = `/${postId}`
      }

      helpers.openSignInByEmailModal()
    },
    messageMeClicked (userId, clickSource) {
      this.wsp.sendRequest({
        data: {
          userId,
          clickSource
        },
        method: 'messageMe.clicked'
      })
    }
  }
}
</script>

<style scoped>
  .button-text {
    max-width: max-content;
    overflow: hidden;
    white-space: nowrap;
    display: block;
    text-overflow: ellipsis;
  }

  .btn-filled {
    @apply bg-primary hover:bg-primary-press shadow-sm text-white font-semibold
  }

  .btn-outline {
    @apply bg-transparent hover:bg-white hover:text-slate-700 shadow-sm text-white border-2 border-white font-semibold
  }
</style>
